import type { TerritorialSearchResult } from "@bonusmap/shared_ui/library";
import { AmbitoInputComponent } from "./ambito-input-component.js";
import { createChoicesInstance } from "../../utils/createChoicesInstance.js";

const { Choices } = window;

/**
 * Variante di AmbitoInput dedicata alla selezione di un singolo comune.
 */
export class ComuneInputComponent extends AmbitoInputComponent {
    static override webComponentName: string = "comune-input";
    protected override _templateId: string = "comune-input";

    constructor() {
        super();
        this.currentSearchType = "comune";
    }

    protected override get initialValueArray(): string[] {
        if (!this.initialValue) return [];
        return [this.initialValue.trim()].filter(Boolean);
    }

    protected override setGlobalListeners(): void {
    }

    protected override async processInitialValue(): Promise<void> {
        await super.processInitialValue();
        const firstValue = this.value[0];
        if (firstValue) {
            // this.setAttribute('value', firstValue); // COMMENTED: causes infinite loop triggering onAttributeChange
        }
    }

    protected override handleClearAll(e: Event): void {
        e.preventDefault();
        if (this.choicesInstance) {
            this.choicesInstance.clearStore();
            this.choicesInstance.clearChoices();
        }
        this._currentSelection = [];
        this.value = [];
        this.removeAttribute('value');
    }

    protected override initializeChoices(): void {
        this.choicesInstance = createChoicesInstance(this.ambitoSelect, {
            removeItemButton: true,
            searchEnabled: true,
            noResultsText: 'Nessun comune trovato',
            noChoicesText: 'Digita almeno un carattere per cercare',
            searchPlaceholderValue: 'Digita per cercare il comune...',
            removeItemIconText: () => 'Rimuovi comune',
            singleModeForMultiSelect: true,
            maxItemCount: 1,
            duplicateItemsAllowed: false,
            closeDropdownOnSelect: true,
            callbackOnCreateTemplates: () => {
                return {
                    item: (config: any, choice: any, removeItemButton: any) => {
                        const originalItem = Choices.defaults.templates.item.call(
                            this.choicesInstance,
                            config,
                            choice,
                            removeItemButton
                        );
                        originalItem.classList.add('comune');
                        return originalItem;
                    }
                };
            }
        });

        this.selectChangeSetupHandler();
        this.selectAddItemSetupHandler();
        this.selectRemoveItemSetupHandler();
        this.selectSearchSetupHandler();
    }

    protected override selectSearchSetupHandler(): void {
      
        this.ambitoSelect.addEventListener('search', (event: any) => {
            const searchTerm = event.detail.value;
            if (searchTerm && searchTerm.length >= 1) {
                this.performSearch(searchTerm, 'comune');
            } else {
                this.choicesInstance.clearChoices();
            }
        });
    }


    protected override syncSelectionFromChoices(): void {
        super.syncSelectionFromChoices();
        const singleValue = this.value[0];
        if (singleValue) {
            // this.setAttribute('value', singleValue); // COMMENTED: causes infinite loop triggering onAttributeChange
            if (!this._currentSelection.length) {
                this.enrichSelection([singleValue]);
            }
        } else {
            // this.removeAttribute('value'); // COMMENTED: causes infinite loop triggering onAttributeChange
        }
    }

    protected override valueChanged(newValue: string[]): void {
        super.valueChanged(newValue);
        const singleValue = newValue[0] || '';
        const selection: TerritorialSearchResult | undefined = this.currentSelection[0];
        this.dispatch('comune-value-changed', {
            value: singleValue,
            selection: selection ?? null
        });
    }

    protected override afterRender(): void {
        if (this.readOnly) {
            super.afterRender();
            return;
        }

        this.ambitoSelect = this.roleElementSelector("ambito-select") as HTMLSelectElement;
        this.initializeChoices();

        this.processInitialValue()
            .then(() => {
                const singleValue = this.value[0];
                if (singleValue) {
                    this.setAttribute('value', singleValue);
                    if (!this._currentSelection.length) {
                        this.enrichSelection([singleValue]);
                    }
                }
            })
            .catch(error => {
                console.error("Error processing initial comune value:", error);
            });
    }

    private async enrichSelection(ids: string[]): Promise<void> {
        if (!ids.length) return;
        try {
            const result = await window.api.getTerritorialByIds(ids);
            const data = (result.data || []).filter((item: TerritorialSearchResult) => !!item?.territorial_id);
            if (!data.length) return;

            const selectionMap = new Map(
                this._currentSelection
                    .filter(item => item?.territorial_id)
                    .map(item => [item.territorial_id as string, item] as const)
            );
            data.forEach(item => {
                const territorialId = item.territorial_id as string;
                if (!selectionMap.has(territorialId)) {
                    selectionMap.set(territorialId, item);
                }
            });

            this._currentSelection = Array.from(selectionMap.values());
        } catch (error) {
            console.error('Error enriching comune selection:', error);
        }
    }
}
